import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';

const fmtPrice = (v) => {
    if (!v && v !== 0) return '—';
    if (v >= 1000) return Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (v >= 1) return Number(v).toLocaleString('en-US', { maximumFractionDigits: 4 });
    return Number(v).toLocaleString('en-US', { maximumFractionDigits: 8 });
};

export default function CoinSearch({ onSelect }) {
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const tRef = useRef(null);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!q || q.length < 1) {
            setResults([]);
            return;
        }
        if (tRef.current) clearTimeout(tRef.current);
        tRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await apiClient.get('/market/search', { params: { q } });
                setResults(data.results || []);
                setOpen(true);
            } catch (err) {
                logger.warn('symbol search failed', err);
            }
            setLoading(false);
        }, 220);
        return () => tRef.current && clearTimeout(tRef.current);
    }, [q]);

    useEffect(() => {
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const pick = (sym) => {
        onSelect?.(sym);
        setQ('');
        setResults([]);
        setOpen(false);
    };

    return (
        <div className="relative w-72 max-w-full" ref={wrapRef} data-testid="coin-search">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
                value={q}
                onChange={(e) => setQ(e.target.value.toUpperCase())}
                onFocus={() => results.length > 0 && setOpen(true)}
                placeholder="Coin ara (BTC, ARB, INJ...)"
                data-testid="coin-search-input"
                className="h-8 pl-8 pr-8 bg-slate-900 border-slate-700 rounded-sm text-xs placeholder:text-slate-400 font-mono uppercase"
            />
            {loading && <Loader2 className="h-3 w-3 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin" />}

            {open && results.length > 0 && (
                <div className="absolute z-40 mt-1 w-full bg-slate-900 border border-slate-700 rounded-sm shadow-2xl max-h-80 overflow-y-auto">
                    {results.map((r) => {
                        const up = r.change_pct_24h >= 0;
                        return (
                            <button
                                key={r.symbol}
                                onClick={() => pick(r.symbol)}
                                data-testid={`coin-search-result-${r.symbol}`}
                                className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-700 text-left border-b border-slate-700/50 last:border-b-0 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="text-slate-50 text-xs font-medium">{r.symbol.replace('USDT', '')}</span>
                                    <span className="text-slate-400 text-[10px] uppercase">USDT</span>
                                </div>
                                <div className="flex items-center gap-3 font-mono text-[11px] tabular-nums">
                                    <span className="text-slate-200">${fmtPrice(r.price)}</span>
                                    <span className={`w-14 text-right ${up ? 'text-emerald-400' : 'text-rose-500'}`}>
                                        {up ? '+' : ''}{r.change_pct_24h.toFixed(2)}%
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
            {open && q && !loading && results.length === 0 && (
                <div className="absolute z-40 mt-1 w-full bg-slate-900 border border-slate-700 rounded-sm px-3 py-2 text-xs text-slate-400">
                    Eşleşen sembol yok
                </div>
            )}
        </div>
    );
}
